/**
 * Velox "About Us" content.
 * Source: copy document provided by Velox.
 */

export interface AboutFounder {
  name: string;
  role: string;
  bio: string;
}

export interface AboutValue {
  title: string;
  body: string;
}

export interface AboutContent {
  back: string;
  eyebrowVision: string;
  vision: string;
  tagline: string;
  eyebrowAbout: string;
  aboutTitle: string;
  aboutBody: string;
  eyebrowMission: string;
  missionTitle: string;
  mission: string;
  eyebrowDifference: string;
  differenceTitle: string;
  values: AboutValue[];
  eyebrowTeam: string;
  teamTitle: string;
  founders: AboutFounder[];
  teamNote: string;
  eyebrowLuxembourg: string;
  luxembourgTitle: string;
  luxembourgBody: string;
  eyebrowFuture: string;
  futureTitle: string;
  futureBody: string;
  ctaTitle: string;
  ctaBody: string;
  ctaPrimary: string;
  ctaSecondary: string;
  callback: {
    title: string;
    description: string;
    name: string;
    company: string;
    email: string;
    phone: string;
    profile: string;
    profileShipper: string;
    profileTransporter: string;
    profileSelect: string;
    message: string;
    messagePlaceholder: string;
    consent: string;
    privacyLink: string;
    submit: string;
    submitting: string;
    cancel: string;
    success: string;
    error: string;
  };
}

const en: AboutContent = {
  back: 'Back to home',
  eyebrowVision: 'Our vision',
  vision: 'A Europe where moving freight is as simple as sending an invoice.',
  tagline: 'Freight Made Simple.',
  eyebrowAbout: 'About Velox',
  aboutTitle: 'Velox is a digital freight orchestration platform built for Europe, starting in the Benelux.',
  aboutBody: `We connect shippers who need goods moved with a network of independent road carriers, and we take responsibility for making that connection work, from the first booking to final delivery.

The freight industry has run on fragmented paperwork, scattered invoices, and slow payments for decades. We think that's solvable with better technology and a simpler commercial model: one platform, one point of contact, one invoice.`,
  eyebrowMission: 'Our mission',
  missionTitle: 'Our mission',
  mission:
    'Our mission is to give every shipper a single, accountable partner for their freight, and every carrier fast, transparent, and fairly priced work. We are starting in the Benelux, with the ambition to scale across Europe.',
  eyebrowDifference: 'What we do differently',
  differenceTitle: 'Most freight platforms introduce two parties and step back. Velox does not.',
  values: [
    {
      title: 'One invoice',
      body: 'We act as the single point of contact for the entire shipment, from pickup to delivery. Whatever carriers are involved, our customers deal with Velox alone and receive one clean, consolidated invoice.',
    },
    {
      title: 'Fixed pricing, no bidding',
      body: 'Loads are booked at a clear, fixed price or assigned directly. Carriers know exactly what a job pays before accepting it, and shippers know exactly what a shipment costs before booking it.',
    },
    {
      title: '48-hour carrier payment',
      body: 'Shipments are covered by cargo insurance and our team manages any claim directly with the shipper. Carriers are paid within forty-eight hours of completing a shipment, a commitment reviewed and confirmed as compliant by Luxembourg’s financial regulator, the CSSF.',
    },
  ],
  eyebrowTeam: 'Who we are',
  teamTitle: 'Founded in Luxembourg by a team spanning logistics, finance, and law',
  founders: [
    {
      name: 'Sinela Gjeçi – de Mahieu',
      role: 'Founder and Chief Executive Officer',
      bio: "Sinela leads Velox's product, operations, and day-to-day growth. Before founding Velox, she ran her own transportation company in Milan, with firsthand exposure to the two problems Velox is built to solve: fragmented accountability for shippers, and slow, unreliable payment for carriers. She holds a second-level Master's degree in Law and has legal experience across private practice and international organisations, including at the United Nations Office of Legal Affairs.",
    },
    {
      name: 'Gatien de Mahieu',
      role: 'Founder and Chief Financial Officer',
      bio: "Gatien leads Velox's financial strategy and partnerships. He brings a background in private banking and wealth management, with prior roles at Patrimundi 1869, Compagnie de Banque Privée Quilvest, KBL European Private Bankers, and BNP Paribas Wealth Management.",
    },
    {
      name: 'Quentin Rutsaert',
      role: 'Co-Founder and Head of Legal',
      bio: 'Quentin brings regulatory and legal expertise to Velox as founder and Managing Director of Rutsaert Legal, a Luxembourg law firm focused on investment funds, securitisation, and corporate finance.',
    },
    {
      name: 'Audric Ranson',
      role: 'Co-Founder and Head of Sales and Partnerships',
      bio: "Audric leads Velox's business development. He brings experience in corporate foreign exchange and payments, including a prior role at Convera.",
    },
  ],
  teamNote:
    "We're a small, founder-led team by design at this stage and close enough to every shipment and every carrier relationship to actually fix what isn't working, rather than manage it from a distance.",
  eyebrowLuxembourg: 'Based in Luxembourg, built for Europe',
  luxembourgTitle: 'Based in Luxembourg, built for Europe',
  luxembourgBody: `Velox Sàrl is incorporated and registered in the Grand Duchy of Luxembourg under RCS Luxembourg number B306257, a jurisdiction chosen for its central position in European logistics and its strong regulatory framework. Velox is building toward Benelux-wide, and ultimately European, coverage.

Velox is supported by Luxembourg's innovation ecosystem, including Technoport and the Cluster for Logistics Luxembourg, and participates in the DeepTech Ventures startup community.`,
  eyebrowFuture: 'Where we are headed',
  futureTitle: 'Where we are headed',
  futureBody:
    "We're building Velox from first principles: a regulator-reviewed payment model, a legal structure that makes us genuinely accountable for the shipments we organise, and the discipline that comes from staying close to the people who actually move freight. We'd rather grow slowly into something shippers and carriers trust completely than grow fast into something they merely tolerate.",
  ctaTitle: 'Get started',
  ctaBody:
    'If you are a shipper looking for a single, reliable point of contact for your freight, or a carrier looking for fixed-price loads and fast, dependable payment, we would like to hear from you.',
  ctaPrimary: 'Join Velox',
  ctaSecondary: 'Request a call',
  callback: {
    title: 'Request a call',
    description: 'Leave your details and our team will call you back to discuss your freight needs.',
    name: 'Full name',
    company: 'Company (optional)',
    email: 'Email',
    phone: 'Phone',
    profile: 'I am',
    profileShipper: 'Shipper',
    profileTransporter: 'Carrier',
    profileSelect: 'Select an option',
    message: 'Message (optional)',
    messagePlaceholder: 'Tell us briefly about your needs or your preferred time slot',
    consent: 'I agree that Velox may use this information to contact me.',
    privacyLink: 'Privacy notice',
    submit: 'Send request',
    submitting: 'Sending...',
    cancel: 'Cancel',
    success: 'Request sent. Our team will call you back shortly.',
    error: 'The request could not be sent. Please try again.',
  },
};

const fr: AboutContent = {
  back: "Retour à l'accueil",
  eyebrowVision: 'Notre vision',
  vision: "Une Europe où transporter du fret est aussi simple qu'envoyer une facture.",
  tagline: 'Le transport, en toute simplicité.',
  eyebrowAbout: 'À propos de Velox',
  aboutTitle: "Velox est une plateforme numérique d'orchestration du fret conçue pour l'Europe, à partir du Benelux.",
  aboutBody: `Nous mettons en relation les expéditeurs qui doivent faire transporter des marchandises avec un réseau de transporteurs routiers indépendants, et nous assumons la responsabilité du bon déroulement de cette relation, de la première réservation à la livraison finale.

Le secteur du transport fonctionne depuis des décennies avec des documents fragmentés, des factures éparpillées et des paiements lents. Nous pensons que c'est un problème soluble grâce à une meilleure technologie et à un modèle commercial plus simple : une plateforme, un interlocuteur, une facture.`,
  eyebrowMission: 'Notre mission',
  missionTitle: 'Notre mission',
  mission:
    "Notre mission est d'offrir à chaque expéditeur un partenaire unique et responsable pour son fret, et à chaque transporteur un travail rapide, transparent et rémunéré à sa juste valeur. Nous démarrons au Benelux, avec l'ambition de nous développer dans toute l'Europe.",
  eyebrowDifference: 'Ce que nous faisons différemment',
  differenceTitle: "La plupart des plateformes mettent deux parties en relation puis s'effacent. Pas Velox.",
  values: [
    {
      title: 'Une seule facture',
      body: "Nous sommes l'interlocuteur unique de toute l'expédition, de l'enlèvement à la livraison. Quels que soient les transporteurs impliqués, nos clients traitent uniquement avec Velox et reçoivent une seule facture consolidée.",
    },
    {
      title: 'Prix fixe, sans enchères',
      body: "Les chargements sont réservés à un prix clair et fixe, ou attribués directement. Les transporteurs savent exactement ce que rapporte une mission avant de l'accepter, et les expéditeurs savent exactement ce que coûte une expédition avant de réserver.",
    },
    {
      title: 'Paiement des transporteurs sous 48 h',
      body: "Les expéditions sont couvertes par une assurance marchandises et notre équipe gère directement toute réclamation avec l'expéditeur. Les transporteurs sont payés dans les quarante-huit heures suivant la fin de l'expédition, un engagement examiné et confirmé conforme par le régulateur financier luxembourgeois, la CSSF.",
    },
  ],
  eyebrowTeam: 'Qui nous sommes',
  teamTitle: 'Fondée au Luxembourg par une équipe issue de la logistique, de la finance et du droit',
  founders: [
    {
      name: 'Sinela Gjeçi – de Mahieu',
      role: 'Fondatrice et Directrice générale',
      bio: "Sinela dirige le produit, les opérations et la croissance quotidienne de Velox. Avant de fonder Velox, elle a dirigé sa propre société de transport à Milan, confrontée directement aux deux problèmes que Velox entend résoudre : la responsabilité fragmentée pour les expéditeurs et les paiements lents et peu fiables pour les transporteurs. Elle est titulaire d'un master de second niveau en droit et possède une expérience juridique en cabinet et au sein d'organisations internationales, notamment au Bureau des affaires juridiques des Nations unies.",
    },
    {
      name: 'Gatien de Mahieu',
      role: 'Fondateur et Directeur financier',
      bio: "Gatien dirige la stratégie financière et les partenariats de Velox. Il apporte une expérience de la banque privée et de la gestion de patrimoine, acquise chez Patrimundi 1869, Compagnie de Banque Privée Quilvest, KBL European Private Bankers et BNP Paribas Wealth Management.",
    },
    {
      name: 'Quentin Rutsaert',
      role: 'Cofondateur et Responsable juridique',
      bio: "Quentin apporte son expertise réglementaire et juridique en tant que fondateur et administrateur délégué de Rutsaert Legal, cabinet luxembourgeois spécialisé en fonds d'investissement, titrisation et financement d'entreprise.",
    },
    {
      name: 'Audric Ranson',
      role: 'Cofondateur et Responsable des ventes et partenariats',
      bio: "Audric dirige le développement commercial de Velox. Il apporte une expérience du change et des paiements aux entreprises, notamment chez Convera.",
    },
  ],
  teamNote:
    "Nous sommes, à ce stade et par choix, une petite équipe dirigée par ses fondateurs, suffisamment proche de chaque expédition et de chaque transporteur pour réellement corriger ce qui ne fonctionne pas, plutôt que de le gérer à distance.",
  eyebrowLuxembourg: 'Basée au Luxembourg, pensée pour l’Europe',
  luxembourgTitle: 'Basée au Luxembourg, pensée pour l’Europe',
  luxembourgBody: `Velox Sàrl est constituée et immatriculée au Grand-Duché de Luxembourg sous le numéro RCS B306257, une juridiction choisie pour sa position centrale dans la logistique européenne et la solidité de son cadre réglementaire. Velox construit une couverture d'abord Benelux, puis européenne.

Velox est soutenue par l'écosystème d'innovation luxembourgeois, notamment Technoport et le Cluster for Logistics Luxembourg, et participe à la communauté de startups DeepTech Ventures.`,
  eyebrowFuture: 'Notre trajectoire',
  futureTitle: 'Notre trajectoire',
  futureBody:
    "Nous construisons Velox à partir de principes fondamentaux : un modèle de paiement examiné par le régulateur, une structure juridique qui nous rend réellement responsables des expéditions que nous organisons, et la discipline de rester proches de celles et ceux qui transportent réellement le fret. Nous préférons grandir lentement vers quelque chose que les expéditeurs et les transporteurs peuvent pleinement croire, plutôt que vite vers quelque chose qu'ils se contentent de tolérer.",
  ctaTitle: 'Commencer',
  ctaBody:
    "Si vous êtes un expéditeur à la recherche d'un interlocuteur unique et fiable pour votre fret, ou un transporteur à la recherche de chargements à prix fixe et de paiements rapides et fiables, nous serions ravis de vous entendre.",
  ctaPrimary: 'Rejoindre Velox',
  ctaSecondary: 'Demander un appel',
  callback: {
    title: 'Demander un appel',
    description: 'Laissez vos coordonnées et notre équipe vous rappelle pour discuter de vos besoins de transport.',
    name: 'Nom complet',
    company: 'Société (optionnel)',
    email: 'E-mail',
    phone: 'Téléphone',
    profile: 'Je suis',
    profileShipper: 'Expéditeur',
    profileTransporter: 'Transporteur',
    profileSelect: 'Sélectionnez une option',
    message: 'Message (optionnel)',
    messagePlaceholder: 'Décrivez brièvement votre besoin ou votre créneau préféré',
    consent: 'J\'accepte que Velox utilise ces informations pour me contacter.',
    privacyLink: 'Politique de confidentialité',
    submit: 'Envoyer la demande',
    submitting: 'Envoi...',
    cancel: 'Annuler',
    success: 'Demande envoyée. Notre équipe vous rappelle rapidement.',
    error: 'La demande n\'a pas pu être envoyée. Veuillez réessayer.',
  },
};

const es: AboutContent = {
  back: 'Volver al inicio',
  eyebrowVision: 'Nuestra visión',
  vision: 'Una Europa donde mover mercancías sea tan sencillo como enviar una factura.',
  tagline: 'Transporte, simplificado.',
  eyebrowAbout: 'Sobre Velox',
  aboutTitle: 'Velox es una plataforma digital de orquestación de transporte creada para Europa, empezando por el Benelux.',
  aboutBody: `Conectamos a los cargadores que necesitan mover mercancías con una red de transportistas de carretera independientes, y asumimos la responsabilidad de que esa conexión funcione, desde la primera reserva hasta la entrega final.

El sector del transporte lleva décadas funcionando con papeleo fragmentado, facturas dispersas y pagos lentos. Creemos que puede resolverse con mejor tecnología y un modelo comercial más sencillo: una plataforma, un interlocutor, una factura.`,
  eyebrowMission: 'Nuestra misión',
  missionTitle: 'Nuestra misión',
  mission:
    'Nuestra misión es ofrecer a cada cargador un socio único y responsable para su transporte, y a cada transportista trabajo rápido, transparente y bien remunerado. Empezamos en el Benelux, con la ambición de crecer por toda Europa.',
  eyebrowDifference: 'Qué hacemos diferente',
  differenceTitle: 'La mayoría de las plataformas presentan a dos partes y se retiran. Velox no.',
  values: [
    {
      title: 'Una sola factura',
      body: 'Somos el punto de contacto único de todo el envío, desde la recogida hasta la entrega. Sean cuales sean los transportistas implicados, nuestros clientes tratan solo con Velox y reciben una única factura consolidada.',
    },
    {
      title: 'Precio fijo, sin pujas',
      body: 'Las cargas se reservan a un precio claro y fijo o se asignan directamente. Los transportistas saben exactamente cuánto paga un trabajo antes de aceptarlo y los cargadores saben cuánto cuesta un envío antes de reservarlo.',
    },
    {
      title: 'Pago al transportista en 48 horas',
      body: 'Los envíos están cubiertos por un seguro de mercancías y nuestro equipo gestiona cualquier reclamación directamente con el cargador. Los transportistas cobran en las cuarenta y ocho horas siguientes a completar el envío, un compromiso revisado y confirmado como conforme por el regulador financiero luxemburgués, la CSSF.',
    },
  ],
  eyebrowTeam: 'Quiénes somos',
  teamTitle: 'Fundada en Luxemburgo por un equipo con experiencia en logística, finanzas y derecho',
  founders: [
    {
      name: 'Sinela Gjeçi – de Mahieu',
      role: 'Fundadora y Consejera Delegada',
      bio: 'Sinela dirige el producto, las operaciones y el crecimiento diario de Velox. Antes de fundar Velox dirigió su propia empresa de transporte en Milán, con experiencia directa en los dos problemas que Velox quiere resolver: la responsabilidad fragmentada para los cargadores y los pagos lentos y poco fiables para los transportistas. Tiene un máster de segundo nivel en Derecho y experiencia jurídica en despachos y organizaciones internacionales, incluida la Oficina de Asuntos Jurídicos de las Naciones Unidas.',
    },
    {
      name: 'Gatien de Mahieu',
      role: 'Fundador y Director Financiero',
      bio: 'Gatien dirige la estrategia financiera y las alianzas de Velox. Aporta experiencia en banca privada y gestión patrimonial, con funciones previas en Patrimundi 1869, Compagnie de Banque Privée Quilvest, KBL European Private Bankers y BNP Paribas Wealth Management.',
    },
    {
      name: 'Quentin Rutsaert',
      role: 'Cofundador y Responsable Jurídico',
      bio: 'Quentin aporta experiencia regulatoria y jurídica como fundador y director general de Rutsaert Legal, despacho luxemburgués especializado en fondos de inversión, titulización y finanzas corporativas.',
    },
    {
      name: 'Audric Ranson',
      role: 'Cofundador y Responsable de Ventas y Alianzas',
      bio: 'Audric dirige el desarrollo de negocio de Velox. Aporta experiencia en divisas y pagos corporativos, incluida una etapa previa en Convera.',
    },
  ],
  teamNote:
    'Somos, por diseño y en esta etapa, un equipo pequeño liderado por sus fundadores, lo bastante cerca de cada envío y de cada transportista como para arreglar de verdad lo que no funciona, en lugar de gestionarlo a distancia.',
  eyebrowLuxembourg: 'Con base en Luxemburgo, construida para Europa',
  luxembourgTitle: 'Con base en Luxemburgo, construida para Europa',
  luxembourgBody: `Velox Sàrl está constituida y registrada en el Gran Ducado de Luxemburgo con el número RCS B306257, una jurisdicción elegida por su posición central en la logística europea y su sólido marco regulatorio. Velox avanza hacia una cobertura en todo el Benelux y, finalmente, europea.

Velox cuenta con el apoyo del ecosistema de innovación luxemburgués, incluidos Technoport y el Cluster for Logistics Luxembourg, y participa en la comunidad de startups DeepTech Ventures.`,
  eyebrowFuture: 'Hacia dónde vamos',
  futureTitle: 'Hacia dónde vamos',
  futureBody:
    'Construimos Velox desde los primeros principios: un modelo de pago revisado por el regulador, una estructura jurídica que nos hace realmente responsables de los envíos que organizamos y la disciplina de mantenernos cerca de quienes mueven la mercancía. Preferimos crecer despacio hacia algo en lo que cargadores y transportistas confíen plenamente que crecer rápido hacia algo que solo toleren.',
  ctaTitle: 'Empezar',
  ctaBody:
    'Si es un cargador que busca un punto de contacto único y fiable para su transporte, o un transportista que busca cargas a precio fijo y pagos rápidos y seguros, nos gustaría saber de usted.',
  ctaPrimary: 'Únete a Velox',
  ctaSecondary: 'Solicitar una llamada',
  callback: {
    title: 'Solicitar una llamada',
    description: 'Déjenos sus datos y nuestro equipo le llamará para hablar de sus necesidades de transporte.',
    name: 'Nombre completo',
    company: 'Empresa (opcional)',
    email: 'Correo electrónico',
    phone: 'Teléfono',
    profile: 'Soy',
    profileShipper: 'Expedidor',
    profileTransporter: 'Transportista',
    profileSelect: 'Seleccione una opción',
    message: 'Mensaje (opcional)',
    messagePlaceholder: 'Cuéntenos brevemente su necesidad o su franja horaria preferida',
    consent: 'Acepto que Velox use esta información para contactarme.',
    privacyLink: 'Política de privacidad',
    submit: 'Enviar solicitud',
    submitting: 'Enviando...',
    cancel: 'Cancelar',
    success: 'Solicitud enviada. Nuestro equipo le llamará en breve.',
    error: 'No se pudo enviar la solicitud. Inténtelo de nuevo.',
  },
};

const de: AboutContent = {
  back: 'Zurück zur Startseite',
  eyebrowVision: 'Unsere Vision',
  vision: 'Ein Europa, in dem Frachttransport so einfach ist wie das Versenden einer Rechnung.',
  tagline: 'Fracht. Einfach gemacht.',
  eyebrowAbout: 'Über Velox',
  aboutTitle: 'Velox ist eine digitale Plattform zur Frachtorchestrierung für Europa – beginnend in den Benelux-Ländern.',
  aboutBody: `Wir verbinden Versender, die Waren transportieren müssen, mit einem Netzwerk unabhängiger Straßentransportunternehmen – und übernehmen die Verantwortung dafür, dass diese Verbindung funktioniert, von der ersten Buchung bis zur finalen Zustellung.

Die Transportbranche arbeitet seit Jahrzehnten mit fragmentierten Unterlagen, verstreuten Rechnungen und langsamen Zahlungen. Das lässt sich mit besserer Technologie und einem einfacheren Geschäftsmodell lösen: eine Plattform, ein Ansprechpartner, eine Rechnung.`,
  eyebrowMission: 'Unsere Mission',
  missionTitle: 'Unsere Mission',
  mission:
    'Unsere Mission ist es, jedem Versender einen einzigen, verantwortlichen Partner für seine Fracht zu geben und jedem Transportunternehmen schnelle, transparente und fair vergütete Aufträge. Wir starten in den Benelux-Ländern mit dem Ziel, europaweit zu wachsen.',
  eyebrowDifference: 'Was wir anders machen',
  differenceTitle: 'Die meisten Plattformen bringen zwei Parteien zusammen und ziehen sich zurück. Velox nicht.',
  values: [
    {
      title: 'Eine Rechnung',
      body: 'Wir sind der einzige Ansprechpartner für die gesamte Sendung – von der Abholung bis zur Zustellung. Unabhängig davon, welche Transportunternehmen beteiligt sind, haben unsere Kunden es nur mit Velox zu tun und erhalten eine einzige, konsolidierte Rechnung.',
    },
    {
      title: 'Festpreise, keine Auktionen',
      body: 'Ladungen werden zu einem klaren Festpreis gebucht oder direkt zugewiesen. Transportunternehmen wissen vor der Annahme genau, was ein Auftrag einbringt, und Versender wissen vor der Buchung genau, was eine Sendung kostet.',
    },
    {
      title: 'Zahlung binnen 48 Stunden',
      body: 'Sendungen sind durch eine Transportversicherung gedeckt, und unser Team wickelt Schadensfälle direkt mit dem Versender ab. Transportunternehmen werden innerhalb von achtundvierzig Stunden nach Abschluss einer Sendung bezahlt – eine Zusage, die von der luxemburgischen Finanzaufsicht CSSF geprüft und als konform bestätigt wurde.',
    },
  ],
  eyebrowTeam: 'Wer wir sind',
  teamTitle: 'In Luxemburg gegründet von einem Team aus Logistik, Finanzwesen und Recht',
  founders: [
    {
      name: 'Sinela Gjeçi – de Mahieu',
      role: 'Gründerin und Geschäftsführerin',
      bio: 'Sinela verantwortet Produkt, Betrieb und das tägliche Wachstum von Velox. Vor der Gründung führte sie ein eigenes Transportunternehmen in Mailand und erlebte die beiden Probleme unmittelbar, die Velox lösen soll: fragmentierte Verantwortlichkeit für Versender und langsame, unzuverlässige Zahlungen für Transportunternehmen. Sie hat einen Master zweiten Grades in Rechtswissenschaften und juristische Erfahrung in Kanzleien und internationalen Organisationen, unter anderem beim Büro für Rechtsangelegenheiten der Vereinten Nationen.',
    },
    {
      name: 'Gatien de Mahieu',
      role: 'Gründer und Finanzvorstand',
      bio: 'Gatien verantwortet Finanzstrategie und Partnerschaften von Velox. Er bringt Erfahrung aus Private Banking und Vermögensverwaltung mit, unter anderem bei Patrimundi 1869, Compagnie de Banque Privée Quilvest, KBL European Private Bankers und BNP Paribas Wealth Management.',
    },
    {
      name: 'Quentin Rutsaert',
      role: 'Mitgründer und Leiter Recht',
      bio: 'Quentin bringt regulatorische und juristische Expertise ein – als Gründer und Managing Director von Rutsaert Legal, einer luxemburgischen Kanzlei mit Fokus auf Investmentfonds, Verbriefung und Unternehmensfinanzierung.',
    },
    {
      name: 'Audric Ranson',
      role: 'Mitgründer und Leiter Vertrieb und Partnerschaften',
      bio: 'Audric verantwortet die Geschäftsentwicklung von Velox. Er bringt Erfahrung im Bereich Devisen und Zahlungsverkehr für Unternehmen mit, unter anderem von Convera.',
    },
  ],
  teamNote:
    'Wir sind derzeit bewusst ein kleines, gründergeführtes Team – nah genug an jeder Sendung und jeder Transporteurbeziehung, um Probleme wirklich zu lösen, statt sie aus der Distanz zu verwalten.',
  eyebrowLuxembourg: 'In Luxemburg zu Hause, für Europa gebaut',
  luxembourgTitle: 'In Luxemburg zu Hause, für Europa gebaut',
  luxembourgBody: `Velox Sàrl ist im Großherzogtum Luxemburg unter der RCS-Nummer B306257 eingetragen – ein Standort, der wegen seiner zentralen Rolle in der europäischen Logistik und seines starken regulatorischen Rahmens gewählt wurde. Velox baut eine Benelux-weite und letztlich europäische Abdeckung auf.

Velox wird vom luxemburgischen Innovationsökosystem unterstützt, unter anderem von Technoport und dem Cluster for Logistics Luxembourg, und ist Teil der Startup-Community DeepTech Ventures.`,
  eyebrowFuture: 'Wohin wir gehen',
  futureTitle: 'Wohin wir gehen',
  futureBody:
    'Wir bauen Velox von Grund auf: ein von der Aufsicht geprüftes Zahlungsmodell, eine Rechtsstruktur, die uns tatsächlich für die von uns organisierten Sendungen verantwortlich macht, und die Disziplin, nah an den Menschen zu bleiben, die Fracht wirklich bewegen. Lieber wachsen wir langsam zu etwas, dem Versender und Transportunternehmen vollständig vertrauen, als schnell zu etwas, das sie nur tolerieren.',
  ctaTitle: 'Loslegen',
  ctaBody:
    'Ob Sie als Versender einen zuverlässigen, einzigen Ansprechpartner für Ihre Fracht suchen oder als Transportunternehmen Ladungen zu Festpreisen und schnelle, verlässliche Zahlungen – wir freuen uns, von Ihnen zu hören.',
  ctaPrimary: 'Velox beitreten',
  ctaSecondary: 'Rückruf anfordern',
  callback: {
    title: 'Rückruf anfordern',
    description: 'Hinterlassen Sie Ihre Kontaktdaten und unser Team ruft Sie zurück, um Ihre Transportbedürfnisse zu besprechen.',
    name: 'Vollständiger Name',
    company: 'Unternehmen (optional)',
    email: 'E-Mail',
    phone: 'Telefon',
    profile: 'Ich bin',
    profileShipper: 'Versender',
    profileTransporter: 'Frachtführer',
    profileSelect: 'Bitte auswählen',
    message: 'Nachricht (optional)',
    messagePlaceholder: 'Beschreiben Sie kurz Ihren Bedarf oder Ihr bevorzugtes Zeitfenster',
    consent: 'Ich stimme zu, dass Velox diese Angaben zur Kontaktaufnahme verwendet.',
    privacyLink: 'Datenschutzerklärung',
    submit: 'Anfrage senden',
    submitting: 'Wird gesendet...',
    cancel: 'Abbrechen',
    success: 'Anfrage gesendet. Unser Team ruft Sie in Kürze zurück.',
    error: 'Die Anfrage konnte nicht gesendet werden. Bitte erneut versuchen.',
  },
};

const it: AboutContent = {
  back: 'Torna alla home',
  eyebrowVision: 'La nostra visione',
  vision: "Un'Europa in cui trasportare merci sia semplice quanto inviare una fattura.",
  tagline: 'Il trasporto, semplificato.',
  eyebrowAbout: 'Chi è Velox',
  aboutTitle: "Velox è una piattaforma digitale di orchestrazione del trasporto merci pensata per l'Europa, a partire dal Benelux.",
  aboutBody: `Colleghiamo i mittenti che devono spostare merci con una rete di trasportatori stradali indipendenti e ci assumiamo la responsabilità che questo collegamento funzioni, dalla prima prenotazione alla consegna finale.

Il settore del trasporto convive da decenni con documenti frammentati, fatture disperse e pagamenti lenti. Crediamo sia un problema risolvibile con una tecnologia migliore e un modello commerciale più semplice: una piattaforma, un interlocutore, una fattura.`,
  eyebrowMission: 'La nostra missione',
  missionTitle: 'La nostra missione',
  mission:
    'La nostra missione è offrire a ogni mittente un partner unico e responsabile per il proprio trasporto e a ogni trasportatore un lavoro rapido, trasparente e correttamente remunerato. Partiamo dal Benelux, con l’ambizione di crescere in tutta Europa.',
  eyebrowDifference: 'Cosa facciamo di diverso',
  differenceTitle: 'La maggior parte delle piattaforme mette in contatto due parti e si ritira. Velox no.',
  values: [
    {
      title: 'Una sola fattura',
      body: "Siamo l'unico punto di contatto per l'intera spedizione, dal ritiro alla consegna. Qualunque sia il trasportatore coinvolto, i nostri clienti trattano solo con Velox e ricevono un'unica fattura consolidata.",
    },
    {
      title: 'Prezzo fisso, nessuna asta',
      body: "I carichi sono prenotati a un prezzo chiaro e fisso oppure assegnati direttamente. I trasportatori sanno esattamente quanto rende un incarico prima di accettarlo e i mittenti sanno quanto costa una spedizione prima di prenotarla.",
    },
    {
      title: 'Pagamento del trasportatore in 48 ore',
      body: "Le spedizioni sono coperte da assicurazione merci e il nostro team gestisce direttamente eventuali reclami con il mittente. I trasportatori sono pagati entro quarantotto ore dal completamento della spedizione, un impegno esaminato e confermato conforme dal regolatore finanziario lussemburghese, la CSSF.",
    },
  ],
  eyebrowTeam: 'Chi siamo',
  teamTitle: 'Fondata in Lussemburgo da un team tra logistica, finanza e diritto',
  founders: [
    {
      name: 'Sinela Gjeçi – de Mahieu',
      role: 'Fondatrice e Amministratrice Delegata',
      bio: "Sinela guida prodotto, operazioni e crescita quotidiana di Velox. Prima di fondare Velox ha gestito una propria società di trasporti a Milano, con esperienza diretta dei due problemi che Velox intende risolvere: responsabilità frammentata per i mittenti e pagamenti lenti e inaffidabili per i trasportatori. Ha un master di secondo livello in Giurisprudenza ed esperienza legale in studi privati e organizzazioni internazionali, incluso l'Ufficio Affari Giuridici delle Nazioni Unite.",
    },
    {
      name: 'Gatien de Mahieu',
      role: 'Fondatore e Direttore Finanziario',
      bio: 'Gatien guida la strategia finanziaria e le partnership di Velox. Porta esperienza nel private banking e nella gestione patrimoniale, con ruoli precedenti in Patrimundi 1869, Compagnie de Banque Privée Quilvest, KBL European Private Bankers e BNP Paribas Wealth Management.',
    },
    {
      name: 'Quentin Rutsaert',
      role: 'Cofondatore e Responsabile Legale',
      bio: "Quentin apporta competenze regolamentari e legali come fondatore e amministratore delegato di Rutsaert Legal, studio legale lussemburghese specializzato in fondi d'investimento, cartolarizzazione e finanza aziendale.",
    },
    {
      name: 'Audric Ranson',
      role: 'Cofondatore e Responsabile Vendite e Partnership',
      bio: 'Audric guida lo sviluppo commerciale di Velox. Porta esperienza nei cambi e nei pagamenti aziendali, incluso un precedente ruolo in Convera.',
    },
  ],
  teamNote:
    'Siamo, per scelta e in questa fase, un piccolo team guidato dai fondatori, abbastanza vicino a ogni spedizione e a ogni trasportatore da risolvere davvero ciò che non funziona, invece di gestirlo a distanza.',
  eyebrowLuxembourg: 'Con sede in Lussemburgo, costruita per l’Europa',
  luxembourgTitle: 'Con sede in Lussemburgo, costruita per l’Europa',
  luxembourgBody: `Velox Sàrl è costituita e registrata nel Granducato di Lussemburgo con numero RCS B306257, una giurisdizione scelta per la posizione centrale nella logistica europea e per il solido quadro regolamentare. Velox punta a una copertura Benelux e, in prospettiva, europea.

Velox è sostenuta dall'ecosistema dell'innovazione lussemburghese, tra cui Technoport e il Cluster for Logistics Luxembourg, e partecipa alla community di startup DeepTech Ventures.`,
  eyebrowFuture: 'Dove stiamo andando',
  futureTitle: 'Dove stiamo andando',
  futureBody:
    'Costruiamo Velox partendo dai principi fondamentali: un modello di pagamento esaminato dal regolatore, una struttura giuridica che ci rende realmente responsabili delle spedizioni che organizziamo e la disciplina di restare vicini a chi muove davvero le merci. Preferiamo crescere lentamente verso qualcosa di cui mittenti e trasportatori si fidino pienamente, piuttosto che velocemente verso qualcosa che si limitino a tollerare.',
  ctaTitle: 'Inizia ora',
  ctaBody:
    'Se sei un mittente alla ricerca di un unico interlocutore affidabile per il tuo trasporto, o un trasportatore in cerca di carichi a prezzo fisso e pagamenti rapidi e sicuri, ci farebbe piacere sentirti.',
  ctaPrimary: 'Unisciti a Velox',
  ctaSecondary: 'Richiedi una chiamata',
  callback: {
    title: 'Richiedi una chiamata',
    description: 'Lascia i tuoi dati e il nostro team ti richiamerà per parlare delle tue esigenze di trasporto.',
    name: 'Nome completo',
    company: 'Azienda (facoltativo)',
    email: 'E-mail',
    phone: 'Telefono',
    profile: 'Sono',
    profileShipper: 'Mittente',
    profileTransporter: 'Trasportatore',
    profileSelect: 'Seleziona un\'opzione',
    message: 'Messaggio (facoltativo)',
    messagePlaceholder: 'Descrivi brevemente la tua esigenza o la fascia oraria preferita',
    consent: 'Accetto che Velox utilizzi queste informazioni per contattarmi.',
    privacyLink: 'Informativa sulla privacy',
    submit: 'Invia richiesta',
    submitting: 'Invio in corso...',
    cancel: 'Annulla',
    success: 'Richiesta inviata. Il nostro team ti richiamerà a breve.',
    error: 'Impossibile inviare la richiesta. Riprova.',
  },
};

const CONTENT: Record<string, AboutContent> = { en, fr, es, de, it };

export const getAboutContent = (language: string): AboutContent =>
  CONTENT[(language || 'fr').split('-')[0]] ?? fr;
